import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn } from 'v5/platform/dom/cn';

const cardVariants = cva('text-fg-default', {
  variants: {
    /** On the raised surface a theme may set apart from the sheet under it (`--color-bg-raised`). */
    raised: {
      false: 'bg-bg-default',
      true: 'bg-bg-raised'
    },
    /** Drawn by a border instead of a shadow. */
    outlined: {
      false: 'rounded-lg shadow',
      true: 'rounded-xl border border-border-default'
    },
    /** It opens or picks something: rests low, lifts while hovered or focused within. */
    interactive: {
      false: '',
      true: 'shadow-xs transition-shadow hocus:shadow-md'
    },
    /** The one of its set shown elsewhere (open beside the list). */
    selected: {
      false: '',
      true: 'ring-2 ring-fg-muted/50'
    }
  },
  defaultVariants: {
    raised: false,
    outlined: false,
    interactive: false,
    selected: false
  }
});

/**
 * A surface for one thing. It has no padding of its own: its sections bring it, so a picture
 * as its first child runs edge to edge (the host clips it with `overflow-hidden`).
 */
const Card = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof cardVariants>
>(({ className, raised, outlined, interactive, selected, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      cardVariants({ raised, outlined, interactive, selected }),
      className
    )}
    {...props}
  />
));
Card.displayName = 'Card';

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-col space-y-1.5 p-5', className)}
    {...props}
  />
));
CardHeader.displayName = 'CardHeader';

const CardTitle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'text-base font-semibold leading-none tracking-tight',
      className
    )}
    {...props}
  />
));
CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('text-base text-fg-muted', className)}
    {...props}
  />
));
CardDescription.displayName = 'CardDescription';

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('p-5 pt-0', className)} {...props} />
));
CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center p-6 pt-0', className)}
    {...props}
  />
));
CardFooter.displayName = 'CardFooter';

const CardValue = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'text-3xl font-semibold leading-none tracking-tight',
      className
    )}
    {...props}
  />
));

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
  CardValue
};
